import React, { useState } from 'react';
import { Button, Input, Form, Alert, Typography } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import { useAppDispatch } from '../../../redux/hooks';
import { setVehicle } from '../../../redux/reducers/vehicleSlice';
import Api from '../../../services/api';
import { BarcodeScanner } from '../../../components/BarcodeScanner';
import Insights from '../../../services/insightsService';

const { Title } = Typography;

interface VinScanStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const VinScanStep: React.FC<VinScanStepProps> = ({ onNext }) => {
  const [mode, setMode] = useState<'scan' | 'manual'>('scan');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const handleScan = () => {
    setShowScanner(true);
  };

  const handleBarcodeScan = async (barcode: string) => {
    console.log('Scanned barcode:', barcode);
    
    // Close scanner first
    setShowScanner(false);
    
    // Small delay to ensure modal closes properly
    setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        // The barcode might be a VIN or vehicle number
        const vehicle = await Api.getVehicle(barcode);

        if (vehicle) {
          dispatch(setVehicle(vehicle));
          onNext();
        }
      } catch (err: unknown) {
        // Check if it's a 404 (vehicle not found) vs 401 (unauthorized)
        const apiErr = err as Error & { statusCode?: number };
        if (apiErr?.statusCode === 404) {
          setError('Vehicle not found. Please try again or enter manually.');
        } else if (apiErr?.statusCode === 401) {
          Insights.trackException(err instanceof Error ? err : new Error(
            typeof err === "object" && err !== null
              ? JSON.stringify(err)
              : String(err)
        ));
          setError('Authentication issue. Please try again.');
        } else {
          Insights.trackException(err instanceof Error ? err : new Error(
            typeof err === "object" && err !== null
              ? JSON.stringify(err)
              : String(err)
          ));
          const errorMessage = apiErr?.message || 'Failed to lookup vehicle from barcode';
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    }, 100);
  };

  const handleManualSubmit = async (values: Record<string, string>) => {
    setLoading(true);
    setError(null);

    try {
      const vehicleNumber = values.vehicleNumber || values.vin;
      const vehicle = await Api.getVehicle(vehicleNumber);

      if (vehicle) {
        dispatch(setVehicle(vehicle));
        onNext();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to lookup vehicle';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="step-container">
      <Title level={5} style={{ textAlign: 'center' }}>Vehicle Scan</Title>

      <BarcodeScanner
        visible={showScanner}
        onScan={handleBarcodeScan}
        onClose={() => setShowScanner(false)}
      />

      {mode === 'scan' ? (
        <div className="scan-container" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '100%' }}>
              <Button 
                type="primary" 
                size="large" 
                icon={<CameraOutlined />}
                iconPosition='end'
                onClick={handleScan}
                loading={loading}
                disabled={loading}
                style={{ width: '100%', height: 60, fontWeight: 'bold' }}
              >
                {loading ? 'Processing...' : 'SCAN BARCODE'}
              </Button>

              {error && (
                <Alert 
                  message={error} 
                  type="warning" 
                  showIcon 
                  style={{ marginTop: 20 }}
                />
              )}
            </div>
          </div>

          <div style={{ paddingBottom: 8 }}>
            <Button 
              size="large" 
              onClick={() => setMode('manual')}
              disabled={loading}
              style={{ color: '#FF9902', width: '100%', fontWeight: 'bold' }}
            >
              MANUAL VEHICLE LOOKUP
            </Button>
          </div>
        </div>
      ) : (
        <div className="manual-entry-container">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleManualSubmit}
            style={{ marginTop: 20 }}
          >
            <Form.Item
              label="Vehicle Number"
              name="vehicleNumber"
              rules={[
                { required: true, message: 'Please enter vehicle number or VIN' },
              ]}
            >
              <Input 
                size="large" 
                placeholder="Enter vehicle number"
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              label="VIN (Optional)"
              name="vin"
              rules={[
                { len: 17, message: 'VIN must be 17 characters' },
              ]}
            >
              <Input 
                size="large" 
                placeholder="Enter 17-character VIN"
                maxLength={17}
                disabled={loading}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>

            {error && (
              <Alert 
                message={error} 
                type="error" 
                showIcon 
                style={{ marginBottom: 20 }}
              />
            )}

            <div className="step-actions">
              <Button size="large" onClick={() => setMode('scan')} disabled={loading} style={{ color: '#FF9902', fontWeight: 'bold' }}>
                BACK TO SCAN
              </Button>
              <Button 
                type="primary" 
                size="large"
                htmlType="submit" 
                loading={loading}
                disabled={loading}
                style={{ fontWeight: 'bold' }}
              >
                {loading ? 'LOOKING UP...' : 'LOOK UP VEHICLE'}
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

export default VinScanStep;